import { useEffect, useRef, useState } from "react";

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function CTASection() {
  const { ref, inView } = useInView();

  return (
    <section id="booking" className="relative bg-[#060606] py-32 px-6 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A84C]/25 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A84C]/25 to-transparent" />

      <div className="max-w-7xl mx-auto">
        <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-16 h-px bg-[#C9A84C]/40" />
            <span className="text-[#C9A84C] text-xs tracking-[0.3em] uppercase font-light font-['Outfit']">Strategy Call</span>
            <div className="w-16 h-px bg-[#C9A84C]/40" />
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-light text-white leading-tight mb-6">
            Book your <em className="text-[#C9A84C] not-italic">strategy call</em>
          </h2>
          <p className="text-white/50 text-base font-light leading-relaxed font-['Outfit'] max-w-xl mx-auto">
            Schedule a free 30-minute strategy session to discuss your expansion into Eastern Europe.
            No obligation — just focused conversation about your goals.
          </p>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start transition-all duration-700 delay-200 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Left: what to expect */}
          <div className="flex flex-col justify-center h-full py-4">
            <p className="text-white/30 text-xs tracking-[0.3em] uppercase font-light font-['Outfit'] mb-8">
              What we cover on the call:
            </p>
            <div className="space-y-6">
              {[
                { icon: "ri-search-eye-line", text: "Market opportunity assessment for your product in CEE" },
                { icon: "ri-route-line", text: "Clear roadmap for Eastern European market entry" },
                { icon: "ri-bar-chart-grouped-line", text: "Revenue potential and deal timeline estimate" },
                { icon: "ri-shield-check-line", text: "How we can act as your dedicated sales team" },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center border border-[#C9A84C]/20 flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-[#C9A84C]/70 text-base`} />
                  </div>
                  <p className="text-white/55 text-sm font-light font-['Outfit'] leading-relaxed pt-1.5">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 border-t border-[#C9A84C]/10 pt-8">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#C9A84C] animate-pulse" />
                <span className="text-white/35 text-xs tracking-[0.25em] uppercase font-light font-['Outfit']">
                  30-minute session · Free · No commitment
                </span>
              </div>
            </div>
          </div>

          {/* Right: Calendar visual + CTA */}
          <div className="border border-[#C9A84C]/15 bg-[#080808]/60 overflow-hidden flex flex-col">
            <div className="w-full h-[520px] overflow-hidden relative">
              <img
                src="https://static.readdy.ai/image/26eb07eb1cfab8d1c82bf00206f32d1a/9c30cb96de049daa9c8daeb76b670ced.png"
                alt="Schedule your strategy call"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#060606] via-transparent to-transparent" />
            </div>
            <div className="px-8 py-8 flex flex-col items-center gap-4 text-center">
              <p className="text-white/40 text-xs tracking-[0.3em] uppercase font-light font-['Outfit']">
                30-minute · Free · No commitment
              </p>
              <a
                href="https://calendly.com/alex-emcalu/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="group w-full flex items-center justify-center gap-3 px-10 py-4 bg-[#C9A84C] text-[#080808] text-sm font-semibold tracking-[0.2em] uppercase hover:bg-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                Book a Strategy Call
                <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
              </a>
              <p className="text-white/25 text-xs font-light font-['Outfit']">
                Opens in Calendly · Pick a time that works for you
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
